import { Box, useTheme } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { ChoiceItemProps } from '@europrocurement/flexy-components';
import { AdresseJsonldTiersReadNoteGroupTimestampableBlameambleGroup } from '@europrocurement/l2d-domain/openApi/ApiTiers';
import { useDispatch } from 'react-redux';
import {
    SousClient,
    SousClientApi,
    SousClientsSelector,
    useTiersService,
    useTiersServiceSelector,
} from '@europrocurement/l2d-domain';
import { AnyAction } from '@reduxjs/toolkit';
import SelectSocieteWrapper from '@europrocurement/l2d-modules/modules/ParcoursFormalite/components/SelectSocieteWrapper';
import { SelectBillingAddressProps } from '../SelectBillingAddress/SelectBillingAddress';
import BillingAddressCard from '../BillingAddressCard/BillingAddressCard';

export type BillingAddressProps = {
    prescriberId: number;
    prescriberBilled: boolean;
    prescriberLabel: string;
    prescriberSiren: string;
    prescriberAddress: string;
} & SelectBillingAddressProps;

const BillingAddress: React.FunctionComponent<BillingAddressProps> = function (props) {
    const {
        prescriberId,
        prescriberBilled,
        prescriberLabel,
        prescriberSiren,
        prescriberAddress,
        clientId,
        onChange: handleChange,
    } = props;
    const { palette } = useTheme();
    const dispatch = useDispatch();
    const { getSousClients } = useTiersService();
    const datasource = useTiersServiceSelector(SousClientsSelector).main;
    const [sousClients, setSousClients] = useState<Array<SousClient>>([]);
    const [, setListFiltered] = useState<Array<ChoiceItemProps>>([]); // liste filtré par la fonction search

    const labelSx = {
        fontSize: '14px',
        fontWeight: '500',
        lineHeight: '25.2px',
        marginBottom: '4px',
    };

    const addressWrapperSx = {
        borderRadius: '12px',
        padding: '6px 20px',
        background: palette.grey[100],
    };

    const addressToString = (
        address: AdresseJsonldTiersReadNoteGroupTimestampableBlameambleGroup,
    ) => {
        let addresseFact = '';
        if (address.adresseApi !== null && address.adresseApi !== '') {
            addresseFact = address.adresseApi ? address.adresseApi : '';
        } else {
            addresseFact = [
                address.adresse1,
                address.adresse2,
                address.adresse3,
                address.adresse4,
                address.codePostal,
                address.ville,
            ]
                .filter(Boolean)
                .join(' ');
        }
        return addresseFact;
    };

    const selectAddressFacturante = (
        addresses: AdresseJsonldTiersReadNoteGroupTimestampableBlameambleGroup[],
    ) => addresses.filter((adresse) => adresse.facturation === true);

    useEffect(() => {
        dispatch({
            type: 'sousClient/resetmainSearch',
        });

        return () => {
            dispatch({
                type: 'sousClient/resetmainSearch',
            });
            dispatch({
                type: 'sousClient/resetmainFilter',
            });
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (datasource.status === 'succeeded') {
            setSousClients(datasource.data);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [datasource.status]);

    // initialisation liste Prescripteur/Societé
    useEffect(() => {
        if (!prescriberId) {
            return;
        }
        dispatch({
            type: 'sousClient/setmainFilter',
            payload: {
                key: 'orSousClientId',
                value: [clientId.toString()],
            },
        });
        dispatch({
            type: 'sousClient/setmainFilter',
            payload: {
                key: 'prescripteurId',
                value: prescriberId,
            },
        });
        dispatch(getSousClients({}) as unknown as AnyAction);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // MAJ sousclient en fonction du datasource etc...
    useEffect(() => {
        if (datasource.status === 'succeeded') {
            const listSC: ChoiceItemProps[] = [];
            sousClients.forEach((sclient) => {
                if (sclient.id === undefined) {
                    return;
                }
                const obj: ChoiceItemProps = {
                    label: sclient.raisonSociale,
                    value: sclient.id.toString(),
                };

                // Récupère le siren en priorité ou le tvaIntracommunautaire
                if (sclient.siren && sclient.siren.trim() !== '') {
                    Object.assign(obj, { idFiscal: `Siren : ${sclient.siren}` });
                } else if (
                    sclient.tvaIntracommunautaire &&
                    sclient.tvaIntracommunautaire.trim() !== ''
                ) {
                    Object.assign(obj, {
                        idFiscal: `Numero d’identification fiscal : ${sclient.tvaIntracommunautaire}`,
                    });
                }

                if (sclient.siren !== '') {
                    Object.assign(obj, { siren: sclient.siren });
                }
                const address = selectAddressFacturante(sclient.adresses);
                if (address.length > 0) {
                    Object.assign(obj, { description: addressToString(address[0]) });
                }

                listSC.push(obj);
            });
            setListFiltered(listSC);
        }
    }, [datasource, sousClients]);

    // va rechercher au changement de societeselected
    useEffect(() => {
        dispatch({
            type: 'sousClient/setmainFilter',
            payload: {
                key: 'orSousClientId',
                value: [clientId.toString()],
            },
        });
        dispatch(getSousClients({}) as unknown as AnyAction);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, clientId]);

    return (
        <Box data-testid="billing-address">
            <Box sx={labelSx}>Adresse de facturation :</Box>
            {prescriberBilled && (
                <Box sx={addressWrapperSx}>
                    <BillingAddressCard
                        label={prescriberLabel}
                        siren={prescriberSiren}
                        address={prescriberAddress}
                    />
                </Box>
            )}
            {!prescriberBilled && (
                <SelectSocieteWrapper
                    onSelect={(selectedValues: SousClientApi[]) => {
                        const clientIdSelected =
                            selectedValues.length > 0 ? selectedValues[0] : undefined;
                        console.log('clientIdSelected is');
                        console.log(clientIdSelected);
                        if (clientIdSelected) {
                            handleChange(clientIdSelected?.id);
                        }
                    }}
                    displayOpere={false}
                    defaultFacturee={clientId}
                    idFormalites={[]}
                    title=""
                />
            )}
        </Box>
    );
};

export default BillingAddress;
